import { styled } from '@mui/material/styles';
import React from 'react';
import { useResolveAsset } from '../context';
import Lightbox from './Lightbox';

type Side = 'left' | 'right';

// Matches the Gallery filmstrip height so a Split photo and a gallery row look
// equivalent. Override per-instance with the `height` prop.
const ROW_H = { xs: 200, sm: 260, md: 300 };

interface SplitProps {
  /** Blog-relative image path, e.g. "images/x.jpg". */
  image: string;
  alt?: string;
  /** Which side the image sits on (desktop). Default: right. */
  side?: Side;
  /** Max image height in px (desktop). Default: the gallery row height (300). */
  height?: number;
  /** The prose that sits beside the image. */
  children?: React.ReactNode;
}

// Desktop: a flex row, columns top-aligned. The image keeps its natural aspect
// ratio and is only capped by max-height (the gallery row height), so it's
// never cropped or stretched; the text takes the remaining width. Following
// content clears below. Mobile: stacks (image on top). $-prefixed props are
// transient (not forwarded to the DOM).
const Row = styled('div', {
  shouldForwardProp: (prop) => prop !== '$side',
})<{ $side: Side }>(({ theme, $side }) => ({
  margin: theme.spacing(3, 0),
  clear: 'both',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    flexDirection: $side === 'right' ? 'row-reverse' : 'row', // media is first in the DOM
    alignItems: 'flex-start',
    gap: theme.spacing(3),
  },
}));

const TextCol = styled('div')({
  flex: '1 1 0',
  minWidth: 0,
  // Collapse the first paragraph's top margin so the text lines up with the
  // top of the image.
  '& > :first-of-type': { marginTop: 0 },
});

const MediaCol = styled('div', {
  shouldForwardProp: (prop) => prop !== '$height',
})<{ $height?: number }>(({ theme, $height }) => ({
  marginBottom: theme.spacing(2), // gap when stacked on mobile
  '& img': {
    display: 'block',
    width: 'auto',
    maxWidth: '100%',
    height: 'auto',
    maxHeight: $height ?? ROW_H.xs,
    borderRadius: theme.shape.borderRadius,
    cursor: 'zoom-in',
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: 0,
    flex: '0 1 auto', // size to the image, but allow shrink on a narrow row
    '& img': { maxHeight: $height ?? ROW_H.sm },
  },
  [theme.breakpoints.up('md')]: {
    '& img': { maxHeight: $height ?? ROW_H.md },
  },
}));

/**
 * Image beside prose. The image keeps its natural aspect ratio and is capped at
 * a max height (the gallery row height by default), so it reads as an
 * equivalent row; the wrapped paragraphs sit in the adjacent column and
 * everything after clears below. Clicking the image opens the shared lightbox.
 *
 *   <Split image="images/x.jpg" alt="…" side="right">
 *
 *   A paragraph beside the photo.
 *
 *   </Split>
 */
const Split: React.FC<SplitProps> = ({ image, alt = '', side = 'right', height, children }) => {
  const resolveAsset = useResolveAsset();
  const [open, setOpen] = React.useState<number | null>(null);
  return (
    <Row $side={side}>
      <MediaCol $height={height}>
        <img src={resolveAsset(image)} alt={alt} loading="lazy" onClick={() => setOpen(0)} />
      </MediaCol>
      <TextCol>{children}</TextCol>
      <Lightbox items={[{ src: image, alt }]} index={open} onIndex={setOpen} />
    </Row>
  );
};

export default Split;
